import { useQueryClient } from '@tanstack/react-query';
import { useRouter, useSearch } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { Field } from '@/components/field';
import { EnvironmentBadge } from '@/components/environment-badge';
import { LanguageSwitch } from '@/components/language-switch';
import { Logo } from '@/components/logo';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { api, errorMessage, setToken, ApiError } from '@/lib/api';
import { rememberLocale, t, tr } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

export function LoginPage() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const search = useSearch({ from: '/login' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [remember, setRemember] = useState(true);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await api.auth.login({ email: email.trim(), password, remember });
      queryClient.clear();
      setToken(res.token);
      const target = search.redirect && search.redirect.startsWith('/') && !search.redirect.startsWith('//') ? search.redirect : '/';
      // The profile's language: when it differs from the page's, the app loads again in it.
      if (res.user.locale && rememberLocale(res.user.locale)) {
        window.location.assign(target.startsWith('/login') ? '/' : target);
        return;
      }
      if (target.startsWith('/login')) await router.navigate({ to: '/' });
      else router.history.push(target);
      void queryClient.invalidateQueries({ queryKey: qk.me });
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 400)) {
        setError(t('Email or password is incorrect. Please try again.'));
      } else {
        setError(errorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative flex min-h-full flex-col bg-background sm:items-center sm:justify-center sm:bg-muted/40 sm:px-4 sm:py-12">
      {/* On a phone the brand fills the top; on a larger screen it is the page's quiet background. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[42vh] bg-[radial-gradient(120%_90%_at_50%_0%,color-mix(in_oklch,var(--primary)_28%,transparent),transparent_70%)] sm:h-full sm:bg-[radial-gradient(60%_50%_at_50%_0%,color-mix(in_oklch,var(--primary)_14%,transparent),transparent_70%)]"
      />
      <div className="absolute top-[max(0.75rem,env(safe-area-inset-top))] right-3 z-10">
        <LanguageSwitch />
      </div>
      <div className="relative mx-auto flex w-full max-w-sm flex-1 flex-col px-6 pt-[max(4.5rem,calc(env(safe-area-inset-top)+3.5rem))] pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:flex-none sm:px-0 sm:pt-0 sm:pb-0">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <Logo className="size-14 drop-shadow-sm sm:size-10" />
          <EnvironmentBadge />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-xl">{t('Welcome to Deskworks')}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t('Sign in to see your desks and agents.')}</p>
          </div>
        </div>
        <Card className="border-0 bg-transparent shadow-none sm:border sm:bg-card sm:shadow-sm">
          <CardContent className="px-0 pt-0 sm:px-6 sm:pt-6">
            <form onSubmit={onSubmit} className="grid gap-4">
              <Field label={t('Email')}>
                <Input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoFocus
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('you@company.com')}
                  className="h-12 sm:h-9"
                />
              </Field>
              <Field label={t('Password')}>
                <Input
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 sm:h-9"
                />
              </Field>
              <label className="flex cursor-pointer items-center gap-2.5 py-1 text-sm select-none">
                <Checkbox checked={remember} onChange={(e) => setRemember(e.target.checked)} className="size-5 sm:size-4" />
                <span>{t('Stay signed in')}</span>
                <span className="text-xs text-muted-foreground">{t('(90 days)')}</span>
              </label>
              {error && (
                <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </p>
              )}
              <Button type="submit" loading={busy} className="h-12 w-full text-base sm:h-9 sm:text-sm">
                {t('Sign in')}
              </Button>
            </form>
          </CardContent>
        </Card>
        <div className="mt-auto pt-8 sm:mt-0 sm:pt-0">
          {/* No link to /register: registration is open only to people who were given its address. */}
          <p className="mt-2 text-center text-xs text-muted-foreground">
            <a href="https://deskworks.ai" className="hover:text-foreground">
              {t('← What is Deskworks?')}
            </a>
          </p>
          {import.meta.env.DEV && (
            <p className="mt-4 text-center text-xs text-muted-foreground">
              {tr('Development mode: test login {email} / {password}', {
                email: <span className="font-mono">admin@deskworks.local</span>,
                password: <span className="font-mono">deskworks</span>,
              })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
